<template>
    <div class="overlayConfigPanel" v-if="overlay">
        <div class="ocp-flyout">
            <div class="ocp-header">
                <p class="ot-title">{{ activeSectionTitle }}</p>
                <Tooltip :text="$t('dash.post-creator.media-editor.overlay-config.close-title')" position="left">
                    <i class="fa-solid fa-xmark ocp-close" @click="$emit('close')"></i>
                </Tooltip>
            </div>

            <div class="ocp-body">
                <template v-if="activeSection === 'content'">
                    <template v-if="overlay.type === 'text'">
                        <div class="ot-row">
                            <input type="text" :placeholder="$t('dash.post-creator.media-editor.overlay-config.field-text')" :value="overlay.data.text" @input="e => patchData({ text: e.target.value })">
                        </div>
                        <div class="ot-row">
                            <label>{{ $t('dash.post-creator.media-editor.overlay-config.font') }}</label>
                            <select :value="overlay.data.fontFamily" @change="e => patchData({ fontFamily: e.target.value })">
                                <option v-for="font in fontOptions" :key="font" :value="font">{{ font }}</option>
                            </select>
                        </div>
                        <div class="ot-row">
                            <label>{{ $t('dash.post-creator.media-editor.overlay-config.size') }}</label>
                            <input type="number" min="10" max="200" :value="overlay.data.fontSize" @input="e => patchData({ fontSize: Number(e.target.value) })">
                        </div>

                        <div class="ot-row ot-format-row">
                            <Tooltip :text="$t('dash.post-creator.media-editor.overlay-config.bold')" :classCustom="'ot-format-btn' + (overlay.data.bold ? ' active' : '')" @click="patchData({ bold: !overlay.data.bold })"><i class="fa-solid fa-bold"></i></Tooltip>
                            <Tooltip :text="$t('dash.post-creator.media-editor.overlay-config.italic')" :classCustom="'ot-format-btn' + (overlay.data.italic ? ' active' : '')" @click="patchData({ italic: !overlay.data.italic })"><i class="fa-solid fa-italic"></i></Tooltip>
                            <Tooltip :text="$t('dash.post-creator.media-editor.overlay-config.underline')" :classCustom="'ot-format-btn' + (overlay.data.underline ? ' active' : '')" @click="patchData({ underline: !overlay.data.underline })"><i class="fa-solid fa-underline"></i></Tooltip>
                            <Tooltip :text="$t('dash.post-creator.media-editor.overlay-config.strikethrough')" :classCustom="'ot-format-btn' + (overlay.data.strikethrough ? ' active' : '')" @click="patchData({ strikethrough: !overlay.data.strikethrough })"><i class="fa-solid fa-strikethrough"></i></Tooltip>
                        </div>

                        <div class="ot-row ot-format-row">
                            <Tooltip :text="$t('dash.post-creator.media-editor.overlay-config.align-left')" :classCustom="'ot-format-btn' + (overlay.data.align === 'left' ? ' active' : '')" @click="patchData({ align: 'left' })"><i class="fa-solid fa-align-left"></i></Tooltip>
                            <Tooltip :text="$t('dash.post-creator.media-editor.overlay-config.align-center')" :classCustom="'ot-format-btn' + (overlay.data.align === 'center' ? ' active' : '')" @click="patchData({ align: 'center' })"><i class="fa-solid fa-align-center"></i></Tooltip>
                            <Tooltip :text="$t('dash.post-creator.media-editor.overlay-config.align-right')" :classCustom="'ot-format-btn' + (overlay.data.align === 'right' ? ' active' : '')" @click="patchData({ align: 'right' })"><i class="fa-solid fa-align-right"></i></Tooltip>
                            <Tooltip :text="$t('dash.post-creator.media-editor.overlay-config.align-justify')" :classCustom="'ot-format-btn' + (overlay.data.align === 'justify' ? ' active' : '')" @click="patchData({ align: 'justify' })"><i class="fa-solid fa-align-justify"></i></Tooltip>
                        </div>

                        <div class="ot-row">
                            <label>{{ $t('dash.post-creator.media-editor.overlay-config.field-text') }}</label>
                            <Tooltip :text="$t('dash.post-creator.media-editor.overlay-config.text-color-tooltip')">
                                <input type="color" :value="overlay.data.color" @input="e => patchData({ color: e.target.value })">
                            </Tooltip>
                            <label>{{ $t('dash.post-creator.media-editor.overlay-config.background') }}</label>
                            <Tooltip :text="$t('dash.post-creator.media-editor.overlay-config.background-color-tooltip')" :classCustom="'ot-color-swatch' + (!overlay.data.backgroundColor ? ' empty' : '')">
                                <input type="color" :value="overlay.data.backgroundColor || '#000000'" @input="e => patchData({ backgroundColor: e.target.value })">
                            </Tooltip>
                            <Tooltip :text="$t('dash.post-creator.media-editor.overlay-config.remove-background-title')">
                                <i class="fa-solid fa-ban ot-clear-bg" @click="patchData({ backgroundColor: '' })"></i>
                            </Tooltip>
                        </div>

                        <div class="ot-row">
                            <label>{{ $t('dash.post-creator.media-editor.overlay-config.icon-before') }}</label>
                            <Tooltip :text="$t('dash.post-creator.media-editor.overlay-config.icon-before-tooltip')" :classCustom="'ot-icon-slot' + (activeIconField === 'prefixIcon' ? ' active' : '')" @click="toggleIconPicker('prefixIcon')">
                                <i v-if="iconIsFa(overlay.data.prefixIcon)" class="fa-solid" :class="'fa-' + overlay.data.prefixIcon.value"></i>
                                <template v-else>{{ iconDisplayChar(overlay.data.prefixIcon) }}</template>
                            </Tooltip>
                            <label>{{ $t('dash.post-creator.media-editor.overlay-config.icon-after') }}</label>
                            <Tooltip :text="$t('dash.post-creator.media-editor.overlay-config.icon-after-tooltip')" :classCustom="'ot-icon-slot' + (activeIconField === 'suffixIcon' ? ' active' : '')" @click="toggleIconPicker('suffixIcon')">
                                <i v-if="iconIsFa(overlay.data.suffixIcon)" class="fa-solid" :class="'fa-' + overlay.data.suffixIcon.value"></i>
                                <template v-else>{{ iconDisplayChar(overlay.data.suffixIcon) }}</template>
                            </Tooltip>
                        </div>
                        <IconPicker
                            v-if="activeIconField"
                            :value="overlay.data[activeIconField]"
                            @select="onSelectIcon"
                        />
                    </template>

                    <template v-if="overlay.type === 'image'">
                        <p class="ct-hint">{{ $t('dash.post-creator.media-editor.overlay-config.image-hint') }}</p>
                    </template>

                    <template v-if="overlay.type === 'shape'">
                        <div class="ot-row">
                            <label>{{ $t('dash.post-creator.media-editor.overlay-config.label-color') }}</label>
                            <input type="color" :value="overlay.data.color" @input="e => patchData({ color: e.target.value })">
                        </div>
                    </template>

                    <template v-if="overlay.type === 'widget'">
                        <div class="ot-row" v-if="overlay.widgetType === 'place'">
                            <input type="text" :placeholder="$t('dash.post-creator.media-editor.overlay-config.place-placeholder')" :value="overlay.data.location" @input="e => patchData({ location: e.target.value })">
                        </div>

                        <template v-if="overlay.widgetType === 'datetime'">
                            <div class="ot-row">
                                <label class="ot-checkbox-label">
                                    <input type="checkbox" :checked="dateTimeIsLive" @change="e => patchData({ useLiveDateTime: e.target.checked })">
                                    {{ $t('dash.post-creator.media-editor.overlay-config.datetime-live') }}
                                </label>
                            </div>
                            <div class="ot-row" v-if="!dateTimeIsLive">
                                <input type="datetime-local" :value="overlay.data.customDateTime || ''" @input="e => patchData({ customDateTime: e.target.value })">
                            </div>
                            <div class="ot-row">
                                <label>{{ $t('dash.post-creator.media-editor.overlay-config.datetime-format') }}</label>
                                <select :value="overlay.data.format || 'datetime'" @change="e => patchData({ format: e.target.value })">
                                    <option value="date">{{ $t('dash.post-creator.media-editor.overlay-config.datetime-format-date') }}</option>
                                    <option value="time">{{ $t('dash.post-creator.media-editor.overlay-config.datetime-format-time') }}</option>
                                    <option value="datetime">{{ $t('dash.post-creator.media-editor.overlay-config.datetime-format-datetime') }}</option>
                                    <option value="full">{{ $t('dash.post-creator.media-editor.overlay-config.datetime-format-full') }}</option>
                                </select>
                            </div>
                        </template>

                        <template v-if="overlay.widgetType === 'weather'">
                            <div class="ot-row">
                                <input type="number" :placeholder="$t('dash.post-creator.media-editor.overlay-config.temperature-placeholder')" :value="overlay.data.temperature" @input="e => patchData({ temperature: Number(e.target.value) })">
                                <label>{{ $t('dash.post-creator.media-editor.overlay-config.unit') }}</label>
                                <select :value="overlay.data.unit" @change="e => patchData({ unit: e.target.value })">
                                    <option value="celsius">°C</option>
                                    <option value="fahrenheit">°F</option>
                                </select>
                            </div>
                            <div class="ot-row">
                                <label>{{ $t('dash.post-creator.media-editor.overlay-config.weather-emoji') }}</label>
                            </div>
                            <div class="ot-emoji-grid">
                                <span
                                    v-for="emoji in weatherEmojiOptions"
                                    :key="emoji"
                                    class="ot-emoji-option"
                                    :class="{ active: (overlay.data.emoji || '☀️') === emoji }"
                                    @click="patchData({ emoji })"
                                >{{ emoji }}</span>
                            </div>
                        </template>

                        <template v-if="overlay.widgetType === 'gif'">
                            <div class="ot-row">
                                <div class="ot-gif-preview" @click="showGifPicker = !showGifPicker">
                                    <img v-if="overlay.data.gifUrl" :src="overlay.data.gifUrl" alt="">
                                    <i v-else class="fa-solid fa-film"></i>
                                    <span>{{ $t('dash.post-creator.media-editor.overlay-config.choose-gif') }}</span>
                                </div>
                            </div>
                            <GifPicker v-if="showGifPicker" :value="overlay.data.gifUrl" @select="onSelectGif" />
                        </template>

                        <div class="ot-row" v-if="overlay.widgetType === 'music'">
                            <input type="text" :placeholder="$t('dash.post-creator.media-editor.overlay-config.music-title-placeholder')" :value="overlay.data.title" @input="e => patchData({ title: e.target.value })">
                        </div>

                        <div class="ot-row" v-if="overlay.widgetType === 'hashtag'">
                            <input type="text" :placeholder="$t('dash.post-creator.media-editor.overlay-config.hashtag-placeholder')" :value="overlay.data.tag" @input="e => patchData({ tag: e.target.value })">
                        </div>

                        <div class="ot-row" v-if="overlay.widgetType === 'mention'">
                            <input type="text" :placeholder="$t('dash.post-creator.media-editor.overlay-config.username-placeholder')" :value="overlay.data.username" @input="e => patchData({ username: e.target.value })">
                        </div>

                        <div class="ot-row">
                            <label>{{ $t('dash.post-creator.media-editor.overlay-config.background') }}</label>
                            <input type="color" :value="overlay.data.bgColor || '#ffffff'" @input="e => patchData({ bgColor: e.target.value })">
                            <label>{{ $t('dash.post-creator.media-editor.overlay-config.field-text') }}</label>
                            <input type="color" :value="overlay.data.textColor || '#2f1937'" @input="e => patchData({ textColor: e.target.value })">
                        </div>
                    </template>
                </template>

                <template v-if="activeSection === 'position'">
                    <Tooltip :text="$t('dash.post-creator.media-editor.overlay-config.reposition-tooltip')" classCustom="ot-reposition" @click="$emit('reposition', overlay.id)">
                        <i class="fa-solid fa-arrows-up-down-left-right"></i> {{ $t('dash.post-creator.media-editor.overlay-config.reposition') }}
                    </Tooltip>

                    <div class="ot-row">
                        <label>{{ $t('dash.post-creator.media-editor.overlay-config.stacking') }}</label>
                    </div>
                    <div class="ot-row ot-format-row ot-stack-row">
                        <Tooltip :text="$t('dash.post-creator.media-editor.overlay-config.stack-front')" classCustom="ot-format-btn ot-stack-btn" @click="$emit('reorder', { overlayId: overlay.id, direction: 'front' })">
                            <i class="fa-solid fa-angles-up"></i>
                            <span class="ot-stack-label">{{ $t('dash.post-creator.media-editor.overlay-config.stack-front-short') }}</span>
                        </Tooltip>
                        <Tooltip :text="$t('dash.post-creator.media-editor.overlay-config.stack-forward')" classCustom="ot-format-btn ot-stack-btn" @click="$emit('reorder', { overlayId: overlay.id, direction: 'forward' })">
                            <i class="fa-solid fa-arrow-up"></i>
                            <span class="ot-stack-label">{{ $t('dash.post-creator.media-editor.overlay-config.stack-forward') }}</span>
                        </Tooltip>
                        <Tooltip :text="$t('dash.post-creator.media-editor.overlay-config.stack-back')" classCustom="ot-format-btn ot-stack-btn" @click="$emit('reorder', { overlayId: overlay.id, direction: 'back' })">
                            <i class="fa-solid fa-angles-down"></i>
                            <span class="ot-stack-label">{{ $t('dash.post-creator.media-editor.overlay-config.stack-back-short') }}</span>
                        </Tooltip>
                        <Tooltip :text="$t('dash.post-creator.media-editor.overlay-config.stack-backward')" classCustom="ot-format-btn ot-stack-btn" @click="$emit('reorder', { overlayId: overlay.id, direction: 'backward' })">
                            <i class="fa-solid fa-arrow-down"></i>
                            <span class="ot-stack-label">{{ $t('dash.post-creator.media-editor.overlay-config.stack-backward') }}</span>
                        </Tooltip>
                    </div>

                    <div class="ot-row" v-if="overlay.type !== 'image'">
                        <label>{{ $t('dash.post-creator.media-editor.overlay-config.scale') }}</label>
                        <Tooltip :text="$t('dash.post-creator.media-editor.overlay-config.scale-tooltip')">
                            <input type="number" min="30" max="250" step="5" :value="Math.round(overlay.transform.scale * 100)" @input="e => patchTransform({ scale: Number(e.target.value) / 100 })">
                        </Tooltip>
                        <span class="ot-unit">%</span>
                    </div>

                    <div class="ot-row" v-if="overlay.type === 'text'">
                        <label>{{ $t('dash.post-creator.media-editor.overlay-config.width-of-zone') }}</label>
                        <Tooltip :text="$t('dash.post-creator.media-editor.overlay-config.width-of-zone-tooltip')">
                            <input type="number" min="10" max="100" step="1" :value="Math.round(overlay.transform.widthPct * 100)" @input="e => patchTransform({ widthPct: Number(e.target.value) / 100 })">
                        </Tooltip>
                        <span class="ot-unit">%</span>
                    </div>

                    <template v-if="overlay.type === 'image'">
                        <div class="ot-row">
                            <Tooltip :text="$t('dash.post-creator.media-editor.overlay-config.lock-aspect-ratio-tooltip')" classCustom="ot-checkbox-label">
                                <input type="checkbox" :checked="imageLockAspectRatio" @change="e => patchTransform({ lockAspectRatio: e.target.checked })">
                                {{ $t('dash.post-creator.media-editor.overlay-config.lock-aspect-ratio') }}
                            </Tooltip>
                        </div>
                        <div class="ot-row">
                            <label>{{ $t('dash.post-creator.media-editor.overlay-config.image-width') }}</label>
                            <Tooltip :text="$t('dash.post-creator.media-editor.overlay-config.image-width-tooltip')">
                                <input type="number" min="5" max="100" step="1" :value="Math.round(overlay.transform.widthPct * 100)" @input="e => onImageWidthChange(Number(e.target.value) / 100)">
                            </Tooltip>
                            <span class="ot-unit">%</span>
                        </div>
                        <div class="ot-row">
                            <label>{{ $t('dash.post-creator.media-editor.overlay-config.image-height') }}</label>
                            <Tooltip :text="$t('dash.post-creator.media-editor.overlay-config.image-height-tooltip')">
                                <input type="number" min="5" max="100" step="1" :value="Math.round(overlay.transform.heightPct * 100)" @input="e => onImageHeightChange(Number(e.target.value) / 100)">
                            </Tooltip>
                            <span class="ot-unit">%</span>
                        </div>
                    </template>

                    <div class="ot-rotation">
                        <label :title="$t('dash.post-creator.media-editor.overlay-config.angle-tooltip')">{{ $t('dash.post-creator.media-editor.overlay-config.angle') }}</label>
                        <input
                            type="range"
                            class="ot-rotation-input"
                            min="0"
                            max="360"
                            step="1"
                            :value="rotationValue"
                            @input="e => patchTransform({ rotationDeg: Number(e.target.value) })"
                        >
                        <div class="ot-rotation-ticks">
                            <template v-for="tick in rotationTicks">
                                <span
                                    :key="'tick-' + tick.deg"
                                    class="ot-tick"
                                    :class="{ major: tick.major }"
                                    :style="{ left: (tick.deg / 360 * 100) + '%' }"
                                ></span>
                                <span
                                    v-if="tick.major"
                                    :key="'label-' + tick.deg"
                                    class="ot-tick-label"
                                    :style="{ left: (tick.deg / 360 * 100) + '%' }"
                                >{{ tick.deg }}°</span>
                            </template>
                        </div>
                    </div>
                </template>

                <template v-if="activeSection === 'appearance' && (overlay.type === 'text' || overlay.type === 'widget')">
                    <p class="ct-hint">{{ $t('dash.post-creator.media-editor.overlay-config.padding-hint') }}</p>
                    <div class="ot-padding-box">
                        <div class="ot-padding-edge ot-padding-edge-top">
                            <Tooltip :text="$t('dash.post-creator.media-editor.overlay-config.padding-top-tooltip')" position="top">
                                <input type="number" min="0" max="200" step="1" :value="paddingTopValue" @input="e => patchData({ paddingTop: Number(e.target.value) })">
                            </Tooltip>
                        </div>
                        <div class="ot-padding-middle">
                            <div class="ot-padding-edge ot-padding-edge-left">
                                <Tooltip :text="$t('dash.post-creator.media-editor.overlay-config.padding-left-tooltip')" position="left">
                                    <input type="number" min="0" max="200" step="1" :value="paddingLeftValue" @input="e => patchData({ paddingLeft: Number(e.target.value) })">
                                </Tooltip>
                            </div>
                            <div class="ot-padding-center">
                                <i class="fa-solid" :class="contentIcon"></i>
                            </div>
                            <div class="ot-padding-edge ot-padding-edge-right">
                                <Tooltip :text="$t('dash.post-creator.media-editor.overlay-config.padding-right-tooltip')" position="right">
                                    <input type="number" min="0" max="200" step="1" :value="paddingRightValue" @input="e => patchData({ paddingRight: Number(e.target.value) })">
                                </Tooltip>
                            </div>
                        </div>
                        <div class="ot-padding-edge ot-padding-edge-bottom">
                            <Tooltip :text="$t('dash.post-creator.media-editor.overlay-config.padding-bottom-tooltip')" position="bottom">
                                <input type="number" min="0" max="200" step="1" :value="paddingBottomValue" @input="e => patchData({ paddingBottom: Number(e.target.value) })">
                            </Tooltip>
                        </div>
                    </div>

                    <p class="ct-hint">{{ $t('dash.post-creator.media-editor.overlay-config.border-hint') }}</p>
                    <div class="ot-row">
                        <label>{{ $t('dash.post-creator.media-editor.overlay-config.thickness') }}</label>
                        <Tooltip :text="$t('dash.post-creator.media-editor.overlay-config.thickness-tooltip')">
                            <input type="number" min="0" max="10" step="1" :value="borderWidthValue" @input="e => patchData({ borderWidth: Number(e.target.value) })">
                        </Tooltip>
                        <label>{{ $t('dash.post-creator.media-editor.overlay-config.label-color') }}</label>
                        <Tooltip :text="$t('dash.post-creator.media-editor.overlay-config.border-color-tooltip')">
                            <input type="color" :value="overlay.data.borderColor || '#000000'" @input="e => patchData({ borderColor: e.target.value })">
                        </Tooltip>
                    </div>
                    <div class="ot-row">
                        <label>{{ $t('dash.post-creator.media-editor.overlay-config.rounding') }}</label>
                        <Tooltip :text="$t('dash.post-creator.media-editor.overlay-config.rounding-tooltip')">
                            <input type="number" min="0" max="100" step="5" :value="Math.round(borderRadiusPctValue * 100)" @input="e => patchData({ borderRadiusPct: Number(e.target.value) / 100 })">
                        </Tooltip>
                        <span class="ot-unit">%</span>
                    </div>

                    <p class="ct-hint">{{ $t('dash.post-creator.media-editor.overlay-config.shadow-hint') }}</p>
                    <div class="ot-row">
                        <Tooltip :text="$t('dash.post-creator.media-editor.overlay-config.shadow-tooltip')" classCustom="ot-checkbox-label">
                            <input type="checkbox" :checked="shadowEnabledValue" @change="e => patchData({ shadowEnabled: e.target.checked })">
                            {{ $t('dash.post-creator.media-editor.overlay-config.enable-shadow') }}
                        </Tooltip>
                    </div>
                    <template v-if="shadowEnabledValue">
                        <div class="ot-row">
                            <label>{{ $t('dash.post-creator.media-editor.overlay-config.label-color') }}</label>
                            <Tooltip :text="$t('dash.post-creator.media-editor.overlay-config.shadow-color-tooltip')">
                                <input type="color" :value="shadowColorHex" @input="e => patchData({ shadowColor: e.target.value })">
                            </Tooltip>
                            <label>{{ $t('dash.post-creator.media-editor.overlay-config.blur') }}</label>
                            <Tooltip :text="$t('dash.post-creator.media-editor.overlay-config.blur-tooltip')">
                                <input type="number" min="0" max="30" step="1" :value="shadowBlurValue" @input="e => patchData({ shadowBlur: Number(e.target.value) })">
                            </Tooltip>
                        </div>
                        <div class="ot-row">
                            <label>{{ $t('dash.post-creator.media-editor.overlay-config.distance') }}</label>
                            <Tooltip :text="$t('dash.post-creator.media-editor.overlay-config.distance-tooltip')">
                                <input type="number" min="0" max="20" step="1" :value="shadowOffsetValue" @input="e => patchData({ shadowOffsetY: Number(e.target.value) })">
                            </Tooltip>
                        </div>
                    </template>
                </template>

                <template v-if="activeSection === 'animation' && !photoMode">
                    <div class="ot-row">
                        <label>{{ $t('dash.post-creator.media-editor.overlay-config.animation-link-mode') }}</label>
                    </div>
                    <div class="ot-row tc-crop-ratios">
                        <span :class="{ active: animationLinkMode === 'continuity' }" @click="setAnimationLinkMode('continuity')">{{ $t('dash.post-creator.media-editor.overlay-config.animation-link-continuity') }}</span>
                        <span :class="{ active: animationLinkMode === 'mirror' }" @click="setAnimationLinkMode('mirror')">{{ $t('dash.post-creator.media-editor.overlay-config.animation-link-mirror') }}</span>
                        <span :class="{ active: animationLinkMode === 'different' }" @click="setAnimationLinkMode('different')">{{ $t('dash.post-creator.media-editor.overlay-config.animation-link-different') }}</span>
                    </div>

                    <p class="ot-title">{{ $t(animationLinkMode === 'different' ? 'dash.post-creator.media-editor.overlay-config.animation-section-in' : 'dash.post-creator.media-editor.overlay-config.animation-section-shared') }}</p>
                    <p class="ct-hint">{{ $t('dash.post-creator.media-editor.overlay-config.animation-style-hint') }}</p>
                    <div class="ot-row tc-crop-ratios">
                        <span :class="{ active: !overlay.animationIn.styles.length }" @click="clearAnimationStyles('in')">{{ $t('dash.post-creator.media-editor.overlay-config.animation-style-none') }}</span>
                        <span v-for="style in animationStyleOptions" :key="'in-' + style.value" :class="{ active: overlay.animationIn.styles.includes(style.value) }" @click="toggleAnimationStyle('in', style.value)">{{ $t(style.labelKey) }}</span>
                    </div>
                    <div class="ot-row tc-crop-ratios" v-if="animationHasDirection(overlay.animationIn.styles)">
                        <span v-for="dir in animationDirectionOptions" :key="'in-' + dir.value" :class="{ active: overlay.animationIn.direction === dir.value }" @click="patchAnimationIn({ direction: dir.value })">{{ $t(dir.labelKey) }}</span>
                    </div>
                    <div class="ot-row" v-if="overlay.animationIn.styles.length">
                        <label>{{ $t('dash.post-creator.media-editor.overlay-config.animation-duration') }}</label>
                        <input type="range" min="100" :max="maxAnimationDurationMs" step="50" :value="overlay.animationIn.durationMs" @input="e => patchAnimationIn({ durationMs: Number(e.target.value) })">
                        <span class="ot-unit">{{ (overlay.animationIn.durationMs / 1000).toFixed(1) }}s</span>
                    </div>

                    <template v-if="animationLinkMode === 'different'">
                        <p class="ot-title">{{ $t('dash.post-creator.media-editor.overlay-config.animation-section-out') }}</p>
                        <div class="ot-row tc-crop-ratios">
                            <span :class="{ active: !overlay.animationOut.styles.length }" @click="clearAnimationStyles('out')">{{ $t('dash.post-creator.media-editor.overlay-config.animation-style-none') }}</span>
                            <span v-for="style in animationStyleOptions" :key="'out-' + style.value" :class="{ active: overlay.animationOut.styles.includes(style.value) }" @click="toggleAnimationStyle('out', style.value)">{{ $t(style.labelKey) }}</span>
                        </div>
                        <div class="ot-row tc-crop-ratios" v-if="animationHasDirection(overlay.animationOut.styles)">
                            <span v-for="dir in animationDirectionOptions" :key="'out-' + dir.value" :class="{ active: overlay.animationOut.direction === dir.value }" @click="patchAnimationOut({ direction: dir.value })">{{ $t(dir.labelKey) }}</span>
                        </div>
                        <div class="ot-row" v-if="overlay.animationOut.styles.length">
                            <label>{{ $t('dash.post-creator.media-editor.overlay-config.animation-duration') }}</label>
                            <input type="range" min="100" :max="maxAnimationDurationMs" step="50" :value="overlay.animationOut.durationMs" @input="e => patchAnimationOut({ durationMs: Number(e.target.value) })">
                            <span class="ot-unit">{{ (overlay.animationOut.durationMs / 1000).toFixed(1) }}s</span>
                        </div>
                    </template>
                    <template v-else>
                        <p class="ct-hint">{{ $t(animationLinkMode === 'continuity' ? 'dash.post-creator.media-editor.overlay-config.animation-continuity-hint' : 'dash.post-creator.media-editor.overlay-config.animation-mirror-hint') }}</p>
                    </template>
                </template>

            </div>

            <div class="ocp-footer-actions">
                <Tooltip :text="$t('dash.post-creator.media-editor.overlay-config.duplicate-tooltip')" classCustom="ot-duplicate" @click="$emit('duplicate-overlay', overlay.id)">
                    <i class="fa-solid fa-clone"></i> {{ $t('dash.post-creator.media-editor.overlay-config.duplicate') }}
                </Tooltip>
                <Tooltip :text="$t('dash.post-creator.media-editor.overlay-config.remove-tooltip')" classCustom="ot-remove" @click="$emit('remove-overlay', overlay.id)">
                    <i class="fa-solid fa-trash"></i> {{ $t('dash.post-creator.media-editor.overlay-config.remove') }}
                </Tooltip>
            </div>
        </div>

        <div class="ocp-rail">
            <Tooltip :text="$t('dash.post-creator.media-editor.overlay-config.section-content')" position="left" :classCustom="'ocp-rail-item' + (activeSection === 'content' ? ' active' : '')" @click="activeSection = 'content'">
                <i class="fa-solid" :class="contentIcon"></i>
            </Tooltip>
            <Tooltip :text="$t('dash.post-creator.media-editor.overlay-config.section-position')" position="left" :classCustom="'ocp-rail-item' + (activeSection === 'position' ? ' active' : '')" @click="activeSection = 'position'">
                <i class="fa-solid fa-arrows-up-down-left-right"></i>
            </Tooltip>
            <Tooltip
                v-if="overlay.type === 'text' || overlay.type === 'widget'"
                :text="$t('dash.post-creator.media-editor.overlay-config.section-appearance')"
                position="left"
                :classCustom="'ocp-rail-item' + (activeSection === 'appearance' ? ' active' : '')"
                @click="activeSection = 'appearance'"
            >
                <i class="fa-solid fa-square"></i>
            </Tooltip>
            <Tooltip
                v-if="!photoMode"
                :text="$t('dash.post-creator.media-editor.overlay-config.section-animation')"
                position="left"
                :classCustom="'ocp-rail-item' + (activeSection === 'animation' ? ' active' : '')"
                @click="activeSection = 'animation'"
            >
                <i class="fa-solid fa-wand-magic-sparkles"></i>
            </Tooltip>
        </div>
    </div>
</template>

<script>
import VueSlider from 'vue-slider-component'
import 'vue-slider-component/theme/antd.css'
import IconPicker from './IconPicker.vue'
import GifPicker from './GifPicker.vue'
import Tooltip from '@/components/dash/tooltip/tooltipInit.vue'

export default {
    name: 'OverlayConfigPanel',

    components: {
        VueSlider,
        IconPicker,
        GifPicker,
        Tooltip
    },

    props: {
        overlay: {
            type: Object,
            default: null
        },
        timelineDurationMs: {
            type: Number,
            default: 5000
        },
        canvasSize: {
            type: Object,
            default: () => ({ width: 1080, height: 1920 })
        },
        photoMode: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            fontOptions: ['Poppins', 'Arial', 'Georgia', 'Courier New', 'Comic Sans MS'],
            activeIconField: null,
            showGifPicker: false,
            activeSection: 'content',
            weatherEmojiOptions: ['☀️', '🌤️', '⛅', '☁️', '🌦️', '🌧️', '⛈️', '🌨️', '🌫️', '💨']
        }
    },

    watch: {
        // Switching to a different overlay (or its type) while a picker is open would otherwise
        // keep patching whichever field/overlay was active on the *previous* selection - and the
        // "Apparence" tab doesn't exist at all for shapes/images, so staying on it after
        // selecting one would show a blank panel.
        overlay() {
            this.activeIconField = null
            this.showGifPicker = false
            if (this.activeSection === 'appearance' && this.overlay.type !== 'text' && this.overlay.type !== 'widget') {
                this.activeSection = 'content'
            }
            if (this.activeSection === 'animation' && this.photoMode) {
                this.activeSection = 'content'
            }
        },

        // Toggling documentMode mid-selection (see MediaEditor.vue's toggleDocumentMode) doesn't
        // change `overlay` itself, so the redirect above alone would leave this tab showing a now
        // hidden, blank animation panel.
        photoMode(isPhotoMode) {
            if (isPhotoMode && this.activeSection === 'animation') this.activeSection = 'content'
        }
    },

    computed: {
        contentIcon() {
            if (this.overlay.type === 'text') return 'fa-font'
            if (this.overlay.type === 'shape') return 'fa-shapes'
            if (this.overlay.type === 'image') return 'fa-image'
            return 'fa-icons'
        },

        activeSectionTitle() {
            if (this.activeSection === 'position') return this.$t('dash.post-creator.media-editor.overlay-config.section-position')
            if (this.activeSection === 'appearance') return this.$t('dash.post-creator.media-editor.overlay-config.section-appearance')
            if (this.activeSection === 'animation') return this.$t('dash.post-creator.media-editor.overlay-config.section-animation')
            return this.$t('dash.post-creator.media-editor.overlay-config.section-content')
        },

        animationLinkMode() {
            return this.overlay.animationLinkMode || 'different'
        },

        // Entrance/exit each capped at half the overlay's duration, so an in+out combo can't exceed it.
        maxAnimationDurationMs() {
            return Math.max(100, Math.floor((this.overlay.endMs - this.overlay.startMs) / 2))
        },

        animationStyleOptions() {
            return [
                { value: 'fade', labelKey: 'dash.post-creator.media-editor.overlay-config.animation-style-fade' },
                { value: 'slide', labelKey: 'dash.post-creator.media-editor.overlay-config.animation-style-slide' },
                { value: 'zoom', labelKey: 'dash.post-creator.media-editor.overlay-config.animation-style-zoom' },
                { value: 'bounce', labelKey: 'dash.post-creator.media-editor.overlay-config.animation-style-bounce' },
                { value: 'rotate', labelKey: 'dash.post-creator.media-editor.overlay-config.animation-style-rotate' },
                { value: 'blur', labelKey: 'dash.post-creator.media-editor.overlay-config.animation-style-blur' }
            ]
        },

        animationDirectionOptions() {
            return [
                { value: 'left', labelKey: 'dash.post-creator.media-editor.overlay-config.animation-direction-left' },
                { value: 'right', labelKey: 'dash.post-creator.media-editor.overlay-config.animation-direction-right' },
                { value: 'up', labelKey: 'dash.post-creator.media-editor.overlay-config.animation-direction-up' },
                { value: 'down', labelKey: 'dash.post-creator.media-editor.overlay-config.animation-direction-down' }
            ]
        },

        // rotationDeg is stored as a free signed value (drag-rotate on the canvas can wind past
        // -180/180) - the slider only needs a stable 0-360 range to show a consistent ruler.
        rotationValue() {
            const deg = (this.overlay && this.overlay.transform.rotationDeg) || 0
            return ((deg % 360) + 360) % 360
        },

        rotationTicks() {
            const ticks = []
            for (let deg = 0; deg <= 360; deg += 15) {
                ticks.push({ deg, major: deg % 90 === 0 })
            }
            return ticks
        },

        // Border/shadow defaults differ by type so the sliders reflect what's actually rendered
        // when the user hasn't touched them yet: pill widgets already had an accent border + soft
        // shadow baked in, plain text had neither (see overlayRenderer.js's fallbacks).
        isWidget() {
            return this.overlay && this.overlay.type === 'widget'
        },

        imageLockAspectRatio() {
            return this.overlay.transform.lockAspectRatio !== false
        },

        // The image's own native ratio (height/width) when known (stored at placement time - see
        // MediaEditor.vue's onStartImagePlacement), falling back to the box's *current* ratio (in
        // real canvas pixels, not just raw percentages, since widthPct/heightPct are relative to
        // different canvas dimensions) so linking still works sensibly even if that's missing.
        imageAspectRatio() {
            const { naturalWidth, naturalHeight } = this.overlay.data
            if (naturalWidth && naturalHeight) return naturalHeight / naturalWidth
            const w = this.overlay.transform.widthPct * this.canvasSize.width
            const h = this.overlay.transform.heightPct * this.canvasSize.height
            return w ? h / w : 1
        },

        // Defaults to true (matches the widget's original always-live-clock behavior) - only
        // false once the user explicitly picks a fixed date/time to display instead.
        dateTimeIsLive() {
            return this.overlay.data.useLiveDateTime !== false
        },

        // Text's horizontal/vertical fallback padding scales with font size (a big caption needs
        // more breathing room than a small one); widgets keep a fixed horizontal fallback since
        // their pill height is already the overlay's own fixed box height, not text-driven.
        defaultPaddingX() {
            if (this.isWidget) return 16
            return Math.max(8, ((this.overlay.data.fontSize || 42)) * 0.3)
        },

        defaultPaddingY() {
            if (this.isWidget) return 0
            return Math.max(6, ((this.overlay.data.fontSize || 42)) * 0.25)
        },

        paddingTopValue() {
            const v = this.overlay.data.paddingTop
            return v !== undefined ? v : Math.round(this.defaultPaddingY)
        },

        paddingRightValue() {
            const v = this.overlay.data.paddingRight
            return v !== undefined ? v : Math.round(this.defaultPaddingX)
        },

        paddingBottomValue() {
            const v = this.overlay.data.paddingBottom
            return v !== undefined ? v : Math.round(this.defaultPaddingY)
        },

        paddingLeftValue() {
            const v = this.overlay.data.paddingLeft
            return v !== undefined ? v : Math.round(this.defaultPaddingX)
        },

        borderWidthValue() {
            const v = this.overlay.data.borderWidth
            return v !== undefined ? v : (this.isWidget ? 2 : 0)
        },

        borderRadiusPctValue() {
            const v = this.overlay.data.borderRadiusPct
            return v !== undefined ? v : (this.isWidget ? 1 : 0)
        },

        shadowEnabledValue() {
            const v = this.overlay.data.shadowEnabled
            return v !== undefined ? v : this.isWidget
        },

        shadowColorHex() {
            const c = this.overlay.data.shadowColor
            return c && c.startsWith('#') ? c : '#000000'
        },

        shadowBlurValue() {
            const v = this.overlay.data.shadowBlur
            return v !== undefined ? v : 10
        },

        shadowOffsetValue() {
            const v = this.overlay.data.shadowOffsetY
            return v !== undefined ? v : 2
        }
    },

    methods: {
        patch(fields) {
            this.$emit('update-overlay', { overlayId: this.overlay.id, patch: fields })
        },

        patchData(fields) {
            this.$emit('update-overlay', { overlayId: this.overlay.id, patch: { data: { ...this.overlay.data, ...fields } } })
        },

        patchTransform(fields) {
            this.$emit('update-overlay', { overlayId: this.overlay.id, patch: { transform: { ...this.overlay.transform, ...fields } } })
        },

        patchAnimationIn(fields) {
            this.patch({ animationIn: { ...this.overlay.animationIn, ...fields } })
        },

        patchAnimationOut(fields) {
            this.patch({ animationOut: { ...this.overlay.animationOut, ...fields } })
        },

        setAnimationLinkMode(mode) {
            this.patch({ animationLinkMode: mode })
        },

        // Styles combine (slide + fade together), so this toggles list membership.
        // Defaults durationMs to 500 when the first style is added, else it'd show no visible effect.
        toggleAnimationStyle(phase, style) {
            const current = phase === 'in' ? this.overlay.animationIn : this.overlay.animationOut
            const styles = current.styles.includes(style)
                ? current.styles.filter(s => s !== style)
                : [...current.styles, style]
            const patch = { styles }
            if (styles.length && !current.durationMs) patch.durationMs = 500
            if (phase === 'in') this.patchAnimationIn(patch)
            else this.patchAnimationOut(patch)
        },

        clearAnimationStyles(phase) {
            if (phase === 'in') this.patchAnimationIn({ styles: [] })
            else this.patchAnimationOut({ styles: [] })
        },

        // Only slide/rotate have a meaningful direction - fade/zoom/bounce don't.
        animationHasDirection(styles) {
            return styles.includes('slide') || styles.includes('rotate')
        },

        // widthPct/heightPct are each relative to their own canvas dimension (width vs height),
        // so keeping the image's ratio locked when only one field changes means converting
        // through real pixels rather than just copying the percentage across.
        onImageWidthChange(widthPct) {
            const patch = { widthPct }
            if (this.imageLockAspectRatio) {
                const widthPx = widthPct * this.canvasSize.width
                patch.heightPct = (widthPx * this.imageAspectRatio) / this.canvasSize.height
            }
            this.patchTransform(patch)
        },

        onImageHeightChange(heightPct) {
            const patch = { heightPct }
            if (this.imageLockAspectRatio) {
                const heightPx = heightPct * this.canvasSize.height
                patch.widthPct = (heightPx / this.imageAspectRatio) / this.canvasSize.width
            }
            this.patchTransform(patch)
        },

        toggleIconPicker(field) {
            this.activeIconField = this.activeIconField === field ? null : field
        },

        onSelectIcon(iconRef) {
            this.patchData({ [this.activeIconField]: iconRef })
            this.activeIconField = null
        },

        onSelectGif(url) {
            this.patchData({ gifUrl: url })
            this.showGifPicker = false
        },

        iconIsFa(iconRef) {
            return !!iconRef && typeof iconRef === 'object' && iconRef.type === 'fa'
        },

        // A prefix/suffix icon can be empty ('' - no icon), a plain emoji string (legacy format),
        // or { type: 'emoji'|'fa', value }. FA icons are rendered as an actual <i> tag elsewhere
        // (see iconIsFa above) - this only needs to cover the two "just print the character" cases.
        iconDisplayChar(iconRef) {
            if (!iconRef) return '—'
            if (typeof iconRef === 'string') return iconRef
            return iconRef.value || '—'
        }
    }
}
</script>

<style lang="scss" scoped></style>
